import { extendTheme } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';

// Define keyframes for animations
const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const theme = extendTheme({
  colors: {
    // ===== UPDATED COLOR SCHEME =====
    // Primary Colors (Blue)
    primary: {
      50: '#ebf8ff',
      100: '#bee3f8',
      200: '#90cdf4',
      300: '#63b3ed',
      400: '#4299e1',
      500: '#3182ce', // Base blue
      600: '#2b6cb0', // Dark blue
      700: '#2c5282', // Darker blue
      800: '#2a4365', // Very dark blue
      900: '#1A365D', // Deep navy (super dark)
    },
    // Secondary Colors (Red)
    secondary: {
      50: '#fff5f5',
      100: '#fed7d7',
      200: '#feb2b2',
      300: '#fc8181',
      400: '#f56565',
      500: '#e53e3e', // Base red
      600: '#c53030', // Dark red
      700: '#9b2c2c', // Darker red
      800: '#822727', // Very dark red
      900: '#63171B', // Deep burgundy (super dark)
    },
    // Accent Colors
    accent: '#F7C548', // Bright Yellow (hope, light)
    hope: '#38B2AC',   // Teal (refreshment, life)
    faith: '#3182ce',  // Matching blue
    love: '#e53e3e',   // Matching red
    background: '#F7FAFC',
    gradientFrom: '#1A365D', // Deep navy blue
    gradientTo: '#822727',   // Deep burgundy red
  },
  fonts: {
    heading: "'Poppins', 'Helvetica Neue', sans-serif",
    body: "'Open Sans', 'Helvetica Neue', sans-serif",
  },
  components: {
    Link: {
      baseStyle: {
        fontWeight: 'bold',
        _hover: {
          textDecoration: 'none',
          color: 'accent',
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease-in-out',
        },
      },
    },
    Button: {
      baseStyle: {
        fontWeight: 'bold',
        borderRadius: 'xl',
      },
      variants: {
        solid: (props) => ({
          bg: props.colorMode === 'dark' ? 'secondary.500' : 'primary.800', // Dark blue in light, bright red in dark
          color: 'white',
          _hover: {
            transform: 'scale(1.05)',
            boxShadow: 'xl',
            bg: props.colorMode === 'dark' ? 'secondary.400' : 'primary.700', // Brighter on hover
            transition: 'all 0.3s ease-in-out',
          },
          _active: {
            animation: `${pulseAnimation} 0.5s ease`,
          }
        }),
        hope: {
          bg: 'hope',
          color: 'white',
          _hover: {
            bg: 'teal.500',
            transform: 'translateY(-3px)',
            boxShadow: '0 10px 20px rgba(56, 178, 172, 0.3)',
          }
        },
        faith: {
          bg: 'primary.600', // Using blue
          color: 'white',
          _hover: {
            bg: 'primary.500',
            transform: 'translateY(-3px)',
            boxShadow: '0 10px 20px rgba(49, 130, 206, 0.4)', // Blue shadow
          }
        },
        love: {
          bg: 'secondary.600', // Using red
          color: 'white',
          _hover: {
            bg: 'secondary.500',
            transform: 'translateY(-3px)',
            boxShadow: '0 10px 20px rgba(229, 62, 62, 0.4)', // Red shadow
          }
        }
      },
    },
    Card: {
      baseStyle: {
        container: {
          borderRadius: 'xl',
          boxShadow: 'md',
          _hover: {
            transform: 'translateY(-5px)',
            boxShadow: 'xl',
            transition: 'all 0.3s ease-in-out',
          }
        }
      }
    }
  },
  styles: {
    global: {
      html: {
        fontSize: '100%',
        scrollBehavior: 'smooth',
      },
      body: {
        margin: 0,
        padding: 0,
        color: 'gray.700',
        bg: 'background',
        fontFamily: "'Open Sans', 'Helvetica Neue', sans-serif",
        bgGradient: 'linear(45deg, gradientFrom, gradientTo, gradientFrom)',
        backgroundSize: '300% 300%',
        animation: `${gradientAnimation} 15s ease infinite`,
      },
      'h1, h2, h3, h4, h5, h6': {
        color: 'primary.800', // Dark blue
        fontWeight: 'bold',
        textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
      },
      'input, textarea': {
        fontFamily: "'Open Sans', 'Helvetica Neue', sans-serif",
        _focus: {
          borderColor: 'accent',
          boxShadow: '0 0 0 2px var(--chakra-colors-accent)',
        }
      },
      a: {
        color: 'primary.700', // Dark blue
        fontWeight: 'semibold',
        _hover: {
          color: 'secondary.600', // Dark red
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease-in-out',
        },
      },
      '::selection': {
        bg: 'accent',
        color: 'white',
      }
    },
  },
});

export default theme;